import { useEffect, useRef } from "react";
import { Room, RemoteParticipant } from "livekit-client";
import { useRoomContext } from "@livekit/components-react";
import type { InterviewContext, TranscriptMessage } from "./livekit-types";

interface UseContextSenderProps {
    isConnected: boolean;
    agentParticipant: RemoteParticipant | null;
    codeContent?: string;
    compilerOutput?: string;
    language?: string;
    participantCount?: number;
    interviewContext?: InterviewContext | null;
    diagramContent?: string;
    transcript: TranscriptMessage[];
    contextSentRef?: React.MutableRefObject<boolean>;
}

/**
 * Custom hook that manages sending context (code, output, candidate info)
 * and chat history to the LiveKit agent via data channel.
 *
 * - Debounces the initial send
 * - Periodic refresh every 3s so agent always has latest code
 * - Sends chat history on initial connection for reconnection continuity
 * - Truncates payloads to stay within LiveKit's 64KB data limit
 */
export function useContextSender({
    isConnected,
    agentParticipant,
    codeContent,
    compilerOutput,
    language,
    participantCount,
    interviewContext,
    diagramContent,
    transcript,
    contextSentRef,
}: UseContextSenderProps) {
    const room = useRoomContext();

    useEffect(() => {
        // Reset context sent flag when agent disconnects
        if (!agentParticipant) {
            if (contextSentRef) {
                contextSentRef.current = false;
            }
            return;
        }

        if (!room || !isConnected) return;

        const sendContext = (includeHistory: boolean) => {
            const now = new Date();
            const context = {
                type: "sandbox_context",
                timestamp: now.toISOString(),
                data: {
                    code: codeContent || "",
                    output: compilerOutput || "",
                    language: language || "Python",
                    participantCount: participantCount || 1,
                    currentDate: now.toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                    }),
                    currentTime: now.toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        hour12: true,
                    }),
                    // Interview Editor context (candidate profile, research, etc.)
                    ...(interviewContext && {
                        interview: {
                            title: interviewContext.title,
                            description: interviewContext.description,
                            instructions: interviewContext.instructions,
                            problemContent: interviewContext.content,
                            difficulty: interviewContext.difficulty,
                            timeLimit: interviewContext.timeLimit,
                            allowedLanguages: interviewContext.allowedLanguages,
                            interviewType: interviewContext.interviewType,
                            candidateName: interviewContext.candidateName,
                            candidateLinkedIn: interviewContext.candidateLinkedIn,
                            candidateDocuments: interviewContext.candidateDocuments,
                            candidateResearchData: interviewContext.candidateResearchData,
                            agentSystemPrompt: interviewContext.agentSystemPrompt,
                        },
                    }),
                    // Diagram/whiteboard content for systems design interviews
                    ...(diagramContent && diagramContent.trim() && { diagram: diagramContent.trim() }),
                },
            };

            try {
                const encoder = new TextEncoder();
                const MAX_DATA_SIZE = 60000; // 60KB to be safe (LiveKit limit is 64KB)

                // Helper to truncate data if too large
                const safeTruncate = (obj: any, maxSize: number): Uint8Array => {
                    let jsonStr = JSON.stringify(obj);
                    let encoded = encoder.encode(jsonStr);

                    if (encoded.length <= maxSize) return encoded;

                    // Truncate large fields progressively
                    const truncated = { ...obj };
                    if (truncated.data) {
                        const data = { ...truncated.data };
                        // Truncate code first (usually largest)
                        if (data.code && data.code.length > 5000) {
                            data.code = data.code.substring(0, 5000) + "\n... (truncated)";
                        }
                        // Truncate output
                        if (data.output && data.output.length > 2000) {
                            data.output = data.output.substring(0, 2000) + "\n... (truncated)";
                        }
                        // Truncate research data
                        if (data.interview?.candidateResearchData && data.interview.candidateResearchData.length > 5000) {
                            data.interview = {
                                ...data.interview,
                                candidateResearchData: data.interview.candidateResearchData.substring(0, 5000) + "\n... (truncated)",
                            };
                        }
                        // Truncate diagram
                        if (data.diagram && data.diagram.length > 3000) {
                            data.diagram = data.diagram.substring(0, 3000) + "\n... (truncated)";
                        }
                        truncated.data = data;
                    }

                    jsonStr = JSON.stringify(truncated);
                    encoded = encoder.encode(jsonStr);

                    // If still too large, aggressive truncation
                    if (encoded.length > maxSize) {
                        const aggressiveTruncated = { ...truncated };
                        if (aggressiveTruncated.data) {
                            aggressiveTruncated.data = {
                                ...aggressiveTruncated.data,
                                code: (aggressiveTruncated.data.code || "").substring(0, 2000),
                                output: (aggressiveTruncated.data.output || "").substring(0, 500),
                            };
                        }
                        jsonStr = JSON.stringify(aggressiveTruncated);
                        encoded = encoder.encode(jsonStr);
                    }

                    return encoded;
                };

                const data = safeTruncate(context, MAX_DATA_SIZE);
                room.localParticipant?.publishData(data, { reliable: true, topic: "context" });

                // Debug: verify interview context is being sent
                if (interviewContext && (interviewContext.candidateName || interviewContext.candidateResearchData)) {
                    console.log("📤 Sent context to agent WITH interview/candidate data:", {
                        candidateName: interviewContext.candidateName,
                        hasResearch: !!interviewContext.candidateResearchData,
                        docCount: interviewContext.candidateDocuments?.length ?? 0,
                    });
                }
                // Debug: verify diagram is being sent
                if (diagramContent && diagramContent.trim()) {
                    console.log("📤 Sent diagram to agent:", diagramContent.trim().length, "chars");
                }

                // Only send chat history on initial connect (for reconnection continuity)
                if (includeHistory && transcript.length > 0) {
                    const recentTranscript = transcript.slice(-50);

                    const historyPayload = {
                        type: "history",
                        timestamp: now.toISOString(),
                        data: recentTranscript.map((msg) => ({
                            text: msg.text,
                            isSelf: msg.sender === "user",
                            timestamp: msg.timestamp,
                        })),
                    };

                    const historyData = safeTruncate(historyPayload, MAX_DATA_SIZE);
                    room.localParticipant?.publishData(historyData, { reliable: true, topic: "context" });
                }
            } catch (err) {
                console.error("❌ Failed to send context/history to agent:", err);
            }
        };

        // Send immediately on agent connect so agent has code before user can ask "can you see the code?"
        // Use 0 delay when we have interview context (candidate profile) so agent gets it ASAP
        const isInitialSend = !contextSentRef?.current;
        const hasInterviewData = !!(interviewContext && (interviewContext.candidateName || interviewContext.candidateResearchData));
        const delay = isInitialSend || hasInterviewData ? 0 : 800;

        const timeoutId = setTimeout(() => {
            sendContext(isInitialSend);
            if (contextSentRef) {
                contextSentRef.current = true;
            }
        }, delay);

        // Safety-net refresh: resend context every 30s in case a send was missed.
        // The effect already re-fires reactively when code/output changes (via deps),
        // so this interval only catches edge cases like the agent connecting late.
        const intervalId = setInterval(() => {
            if (room && isConnected && agentParticipant) {
                sendContext(false);
            }
        }, 30000);

        return () => {
            clearTimeout(timeoutId);
            clearInterval(intervalId);
        };
    }, [room, isConnected, agentParticipant, codeContent, compilerOutput, language, participantCount, interviewContext, diagramContent]);
}
